import { InputType, PreviewMediaType } from '@/types';

import type { IInput } from '@/types';

interface SheetCopy {
  title: string;
  subtitle: string;
}

interface GetSheetCopyParams {
  inputs: IInput[];
  presetType: PreviewMediaType | undefined;
}

const PHOTOS_SUBTITLE = 'Add clear, well-lit photos';
const PHOTO_AND_PROMPT_SUBTITLE = 'Add one photo and describe the scene';
const VIDEO_AND_PROMPT_SUBTITLE = 'Add one video and describe the scene';
const VIDEO_SUBTITLE = 'Add one video';
const PROMPT_ONLY_SUBTITLE = 'Describe the scene you want';
const FALLBACK_SUBTITLE = 'Complete the details below';

// `presetType` is undefined only before the preset loads; the sheet opens
// after `loadActivePreset` populates the store, so the fallback is
// practically unreachable.
const getCreateTitle = (presetType: PreviewMediaType | undefined): string =>
  presetType === PreviewMediaType.IMAGE
    ? 'Create your image'
    : 'Create your video';

const getSheetCopy = ({
  inputs,
  presetType,
}: GetSheetCopyParams): SheetCopy => {
  const imageCount = inputs.filter(
    (input) => input.type === InputType.IMAGE
  ).length;
  const videoCount = inputs.filter(
    (input) => input.type === InputType.VIDEO
  ).length;
  const hasTextInput = inputs.some((input) => input.type === InputType.TEXT);
  const createTitle = getCreateTitle(presetType);

  if (imageCount >= 2 && videoCount === 0) {
    return {
      title: imageCount === 2 ? 'Add two photos' : `Add ${imageCount} photos`,
      subtitle: PHOTOS_SUBTITLE,
    };
  }

  if (imageCount === 1 && videoCount === 0 && hasTextInput) {
    return { title: createTitle, subtitle: PHOTO_AND_PROMPT_SUBTITLE };
  }

  if (videoCount >= 1 && imageCount === 0) {
    if (videoCount === 1) {
      return {
        title: createTitle,
        subtitle: hasTextInput ? VIDEO_AND_PROMPT_SUBTITLE : VIDEO_SUBTITLE,
      };
    }
    return { title: createTitle, subtitle: `Add ${videoCount} videos` };
  }

  if (hasTextInput && imageCount === 0 && videoCount === 0) {
    return { title: createTitle, subtitle: PROMPT_ONLY_SUBTITLE };
  }

  return { title: createTitle, subtitle: FALLBACK_SUBTITLE };
};

export default getSheetCopy;
